import React, { useState, useEffect } from "react";
import questionsJson from "./questions.json";
import { Progress, Radio, Button } from "antd";
import "./TraineeDashboard.css";
import { GlassConfirmModal, GlassSuccessModal } from "../../components/models/GlassModals";
import { logOut, useApiCall } from "../../services/api";
import { useSelector } from "react-redux";
import { useToast } from "../../utils/Toast";
import useExpiredToken from "../../utils/useExpiredToken";

const TraineeDashboard = () => {
  const { callApi } = useApiCall();
  const toast = useToast();
  const expireToken = useExpiredToken();
  const token = useSelector((state) => state.auth.jwtToken);

  const [hasStarted, setHasStarted] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timer, setTimer] = useState(5400);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return [h, m, s].map((v) => (v < 10 ? "0" + v : v)).join(":");
  };

  const handleOptionChange = (e) => {
    setAnswers({
      ...answers,
      [currentQuestionIndex]: {
        question: currentQuestion,
        answer: e.target.value,
      },
    });
  };

  const handleFinishAction = () => {
    if (currentQuestionIndex === questions?.length - 1) {
      setIsConfirmOpen(true);
    } else {
      handleQuestionChange("next");
    }
  };

  const handleLogout = async () => {
    const res = await logOut("auth/logout", token, {});
    if (res) {
      setTimeout(() => {
        expireToken();
      }, 1000);
    }
  };

  const submitAssessment = async () => {
    const payload = {
      responses: Object.values(answers)?.map((a) => ({
        question_id: a.question.id,
        selected_option: a.answer,
      })),
    };

    const { status } = await callApi({
      endpoint: `superadmin/master/save-trainee-responses`,
      bodyData: payload,
      apiMethod: "post",
    });

    if (status) {
      setIsConfirmOpen(false);
      setIsSuccessOpen(true);
      setTimeout(() => {
        handleLogout();
      }, 3000);
    }
  };

  const handleQuestionChange = (direction, e) => {
    if (direction === "next" && currentQuestionIndex === questions?.length - 1) return;
    if (direction === "prev" && currentQuestionIndex === 0) return;

    if (e && e.key && e.key !== "Enter") return;

    setCurrentQuestionIndex((prev) => (direction === "next" ? prev + 1 : prev - 1));
  };

  const fetchQuestions = async () => {
    const { decrypted, status } = await callApi({
      endpoint: `superadmin/master/get-random-pktquestions`,
      showToast: false,
    });

    if (status) {
      setQuestions(decrypted?.data || questions);
    } else {
      toast.error(decrypted.message);
      handleLogout();
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (!hasStarted || timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [hasStarted, timer]);

  useEffect(() => {
    document.title = "Nova";

    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = "/assets/small-questions-logo.png";
  }, []);

  const answeredCount = Object.keys(answers)?.length;
  const progressPercent = (answeredCount / questions?.length) * 100;
  const currentQuestion = questions[currentQuestionIndex] || {};

  return (
    <div className="dashboard-wrapper">
      <div className="app-logo">
        <img src="/assets/questions-logo.png" height={80} width="auto" alt="Logo" />
      </div>

      {!hasStarted ? (
        <div className="glass-container container-fluid rules-container">
          <div className="d-flex align-items-start gap-3 mb-4">
            <div className="mt-1 text-white">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path
                  fillRule="evenodd"
                  d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-white fw-bold mb-1">Before you begin</h4>
              <p className="text-light-gray mb-0">
                Please review the details below before starting your screening.
              </p>
            </div>
          </div>

          <div className="info-box d-flex align-items-start gap-3 mb-5">
            <div className="mt-1 text-white">
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                <path d="M8.93 6.588l-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
              </svg>
            </div>
            <div className="text-white">
              <div className="fw-semibold">You won't be able to skip or pause once started.</div>
              <div className="text-light-gray">Please answer based on your own experience.</div>
            </div>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-md-6">
              <div className="rule-card h-100">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="rule-icon-wrapper">
                    <svg width="20" height="20" fill="white" viewBox="0 0 16 16">
                      <path d="M14.5 3a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h13zm-13-1A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2h-13z" />
                      <path d="M3 5.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zM3 8.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zM3 11.5a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5z" />
                    </svg>
                  </div>
                  <div>
                    <h6 className="text-white fw-bold mb-0">Screening Overview</h6>
                    <small className="text-light-gray">
                      Helps us understand your skills and decision-making.
                    </small>
                  </div>
                </div>
                <ul className="rule-list">
                  <li>Role-based multiple-choice questions.</li>
                  <li>Takes about 90-60 minutes.</li>
                  <li>Best completed in one sitting.</li>
                </ul>
              </div>
            </div>

            <div className="col-md-6">
              <div className="rule-card h-100">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="rule-icon-wrapper">
                    <svg width="20" height="20" fill="white" viewBox="0 0 16 16">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                    </svg>
                  </div>
                  <div>
                    <h6 className="text-white fw-bold mb-0">Requirements</h6>
                    <small className="text-light-gray">
                      Ensure these basics are in place before you begin.
                    </small>
                  </div>
                </div>
                <ul className="rule-list">
                  <li>Reliable internet connection.</li>
                  <li>60-90 minutes of uninterrupted time.</li>
                  <li>A quiet place to focus.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end mt-5">
            <Button
              type="primary"
              className="finish-btn d-flex align-items-center gap-2"
              onClick={() => setHasStarted(true)}
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                <path d="M6.271 5.055a.5.5 0 0 1 .52.038l3.5 2.5a.5.5 0 0 1 0 .814l-3.5 2.5A.5.5 0 0 1 6 10.5v-5a.5.5 0 0 1 .271-.445z" />
              </svg>
              Begin Screening
            </Button>
          </div>
        </div>
      ) : (
        <div className="glass-container container-fluid">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h3 className="text-white fw-bold mb-1">Screening Questions</h3>
              <p className="text-light-gray mb-0">
                Answer the following questions based on your experience.
              </p>
            </div>
            <div className="text-white d-flex align-items-center gap-2 fw-semibold">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z" />
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0" />
              </svg>
              {formatTime(timer)} left
            </div>
          </div>

          <div className="d-flex align-items-center gap-3 mb-5">
            <div className="flex-grow-1">
              <Progress
                percent={progressPercent}
                showInfo={false}
                strokeColor="#ffffff"
                trailColor="rgba(255, 255, 255, 0.2)"
                size="small"
              />
            </div>
            <div className="text-white fw-bold">Final Question</div>
          </div>

          <div className="question-badge text-white mb-4">
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
              <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286m1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94" />
            </svg>
            Question {currentQuestionIndex + 1}
          </div>

          <h5 className="text-white mb-4">
            {currentQuestion.question_text || "Loading question..."}
          </h5>

          <div className="answer-container mb-4">
            {(() => {
              switch (currentQuestion.question_type) {
                case "MCQ":
                  return (
                    <Radio.Group
                      onChange={handleOptionChange}
                      value={answers[currentQuestionIndex]?.answer}
                      className="w-100 d-flex flex-column gap-3"
                    >
                      {currentQuestion.pkt_options?.map((opt, idx) => (
                        <div
                          key={idx}
                          className={`glass-option ${answers[currentQuestionIndex]?.answer === opt.option_text ? "selected" : ""}`}
                          onClick={() => handleOptionChange({ target: { value: opt.option_text } })}
                        >
                          <Radio value={opt.option_text}>{opt.option_text}</Radio>
                        </div>
                      ))}
                    </Radio.Group>
                  );

                case "FILL_BLANK":
                  return (
                    <div className="glass-input-wrapper">
                      <input
                        type="text"
                        className="glass-text-input"
                        placeholder="Enter the right answer"
                        value={answers[currentQuestionIndex]?.answer || ""}
                        onChange={(e) => handleOptionChange({ target: { value: e.target.value } })}
                        onKeyDown={(e) => handleQuestionChange("next", e)}
                      />
                    </div>
                  );

                case "Short_Form_Answer":
                  return (
                    <div className="glass-input-wrapper">
                      <textarea
                        className="glass-text-input glass-textarea"
                        placeholder="Enter the short form answer"
                        rows={4}
                        value={answers[currentQuestionIndex]?.answer || ""}
                        onChange={(e) => handleOptionChange({ target: { value: e.target.value } })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleQuestionChange("next", e);
                          }
                        }}
                      />
                    </div>
                  );

                default:
                  return <p className="text-white">Unsupported question type</p>;
              }
            })()}
          </div>

          <div className="d-flex justify-content-between mt-4 w-100">
            <Button
              type="primary"
              className="prev-btn"
              onClick={() => handleQuestionChange("prev")}
              disabled={currentQuestionIndex === 0}
            >
              Prev
            </Button>
            <Button
              type="primary"
              className="finish-btn"
              onClick={handleFinishAction}
              disabled={!answers[currentQuestionIndex]?.answer}
            >
              {currentQuestionIndex === questions?.length - 1 ? "Finish" : "Next"}
            </Button>
          </div>
        </div>
      )}

      <GlassConfirmModal
        open={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={submitAssessment}
      />

      <GlassSuccessModal open={isSuccessOpen} onClose={() => setIsSuccessOpen(false)} />
    </div>
  );
};

export default TraineeDashboard;
